import dayjs from 'dayjs';
import { useEffect, useState } from 'react';

import { TableSort } from './SortHeader';

interface SortOption {
  key: string;
  typeConvert?: 'date' | 'day' | undefined;
}

const useSorter = <T extends object>(
  list: T[],
  sortOptions: SortOption[],
  defaultSort: TableSort
) => {
  const [sortedList, setSortedList] = useState(list);

  const [activeSort, setActiveSort] = useState<TableSort>(defaultSort);

  // Stringify list to avoid infinite re-renders in useEffect dep array
  const stringifiedList = JSON.stringify(list);

  // Sort list when sortKey changes
  useEffect(() => {
    const sorted = [...list];
    const sorter = createSorter<T>(sortOptions, activeSort);
    sorted.sort(sorter);
    setSortedList(sorted);
    // Do not put entire list here (or any other array)
    // causes infinite loop
  }, [activeSort, stringifiedList]);

  return { activeSort, setActiveSort, sortedList } as const;
};

function createSorter<T extends object>(
  sortOptions: SortOption[],
  sortKey: TableSort
) {
  const sortOption = sortOptions.find(({ key }) => key === sortKey.key);

  return (a: T, b: T) => {
    if (sortOption) {
      const { key } = sortOption;
      const type = sortOption.typeConvert;

      const aKey = (a as any)[key];
      const bKey = (b as any)[key];

      if (sortKey.direction === 'asc') {
        return compare(aKey, bKey, type);
      }
      if (sortKey.direction === 'desc') {
        return compare(bKey, aKey, type);
      }
    }
    return 0;
  };
}

function compare(
  a: string | Date | number | undefined,
  b: string | Date | number | undefined,
  type: 'day' | 'date' | undefined
) {
  if (typeof a === 'undefined') return 1;
  if (typeof b === 'undefined') return -1;

  if (type === 'date') {
    return dayjs(a).diff(dayjs(b));
  }
  if (type === 'day') {
    return dayjs(a).diff(dayjs(b), 'day');
  }
  if (typeof a === 'string' && typeof b === 'string') {
    return a.localeCompare(b);
  }
  if (a > b) {
    return 1;
  }
  if (a < b) {
    return -1;
  }
  return 0;
}

export default useSorter;
